import { EquipmentIcon, PassIcon, ReceptionBellIcon, ShirtIcon, UserIcon } from "@xola/icons";
import clsx from "clsx";
import {
    DateFormat,
    DemographicIcon,
    formatDate,
    hasOtaTicket,
    isEquipmentItem,
    isExperiencePurchaseItem,
    isGiftPurchaseItem,
    isMembershipItem,
    isMerchandiseItem,
    isMistPurchase,
    isOtherSellerPurchase,
} from "common";
import dayjs, { Dayjs } from "dayjs";
import { sumBy } from "lodash";
import pluralize from "pluralize";
import React, { memo, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    BookingEngineSource,
    GiftPurchaseItem,
    LineItemType,
    Package,
    PartnerPackage,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
    Seller,
    Unit,
} from "types";
import { NoTranslate } from "../Common/NoTranslate";
import { SourceIcon } from "../Common/SourceIcon";
import { BalanceBadge } from "../Dashboard/Common/BalanceBadge";
import { PurchaseDisputeBadge } from "./PurchaseDisputeBadge";
import { PurchaseProductImage } from "./PurchaseProductImage";
import { StatusIcon } from "./PurchaseStatusIcon";
import { PurchaseTileBreakdown } from "./PurchaseTileBreakdown";
import { SellerLogo } from "@/components/Common/SellerLogo";
import { useResource } from "@/hooks/useResource";
import { isDefaultDemographic } from "@/models/DemographicUnit";
import { getMerchandisePurchaseItems, hasUnfulfilledMerchandise } from "@/models/MerchandisePurchaseItem";
import { isSharedDemographicsPackage } from "@/models/Package";
import {
    getCustomerName,
    getDisplayItem,
    isAffiliatePurchase,
    isDisputeSubmitted,
    isGift,
    isPackageItem,
} from "@/models/Purchase";
import {
    getScheduledExperiencePurchaseItem,
    getScheduledExperiencePurchaseItems,
    getShortCode,
} from "@/models/PurchaseItem";
import { usePurchasePageStore } from "@/stores/usePurchasePageStore";
import { getSeller } from "@/utils/getSeller";

interface PurchaseListItemProps {
    purchase: Purchase;
    isActive?: boolean;
    selectedItem?: PurchaseItem;
}

export const PurchaseListItem = ({ purchase, isActive = false, selectedItem }: PurchaseListItemProps) => {
    const [isOpen, setIsOpen] = useState(isActive);
    const isMist = purchase.items?.length > 1;
    const history = useHistory();
    const { data: partnerSeller } = useResource<Seller>(["sellers", purchase.seller?.id], null, {
        enabled: isOtherSellerPurchase(purchase, getSeller().id),
    });

    useEffect(() => {
        if (!isActive) {
            setIsOpen(false);
        }
    }, [isActive]);

    if (!purchase.items?.length) {
        return null;
    }

    const handleClick = (purchase: Purchase, item?: PurchaseItem) => {
        const newItemId = item?.id ?? "";
        // Updating last selected item id in case user wants to see purchase level info for MIST purchases
        usePurchasePageStore.setState({ lastSelectedItem: newItemId });
        history.push(`/purchases/${purchase.id}/${newItemId}`);
    };

    const handlePurchaseClick = () => {
        if (isMist) {
            setIsOpen(!isOpen);
            handleClick(purchase);
        } else {
            const item = selectedItem ? purchase.items.find((item) => item.id === selectedItem.id) : null; // selectedItem might not be owned this purchase
            handleClick(purchase, item ?? purchase.items[0]);
        }
    };

    const showPurchaseDate = !isMist || (isMist && !isOpen);

    return (
        <>
            <div
                data-testid="purchase-list-item"
                className={clsx(
                    "flex cursor-pointer p-4 pr-2 hover:bg-blue-lighter",
                    isActive && !isMist && "bg-blue-lighter",
                    isActive && isMist && "bg-gray-lighter",
                )}
                onClick={handlePurchaseClick}
            >
                <PurchaseProductImage purchase={purchase} logo={getLogo(purchase, partnerSeller)} />
                <PurchaseInfo purchase={purchase} item={purchase.items[0]} showDate={showPurchaseDate} />
            </div>

            {isMist && isOpen && <PurchaseTileBreakdown purchase={purchase} selectedItem={selectedItem} />}
        </>
    );
};

// Gifts have no short code, so a gift sold without names is identified by its gift code.
const getPurchaseListTitle = (item: PurchaseItem) => {
    if (isGiftPurchaseItem(item)) {
        const giftCode = item.couponCode?.code;
        return giftCode ? `Gift - ${giftCode.toUpperCase()}` : "Gift";
    }

    return getShortCode(item)?.toUpperCase();
};

const PurchaseInfo = ({ purchase, item, showDate }: { purchase: Purchase; item: PurchaseItem; showDate: boolean }) => {
    const isPackage = isPackageItem(item);
    const isMerchandise = isMerchandiseItem(item);
    const purchaseDate = getPurchaseDate(purchase);

    return (
        <div className="flex w-full flex-col space-y-1" data-testid={item.object}>
            <div className="flex items-center justify-between text-sm lg:text-base xl:h-auto">
                <span className="w-40 truncate font-bold" data-testid="purchase-list-item-customer-name">
                    <NoTranslate>
                        {purchase.customerName
                            ? getCustomerName(purchase)
                            : (!isMistPurchase(purchase) || isPackage || isMerchandise) &&
                              getPurchaseListTitle(purchase.items[0])}
                    </NoTranslate>
                    {isPackage && " (Package)"}
                </span>

                {showDate && (
                    <NoTranslate
                        as="div"
                        className={clsx("whitespace-nowrap text-xs", { "mr-3": purchaseDate.length < 7 })}
                    >
                        {purchaseDate}
                    </NoTranslate>
                )}
            </div>
            {isPackage ? (
                <PackageIconRow purchase={purchase} item={item} />
            ) : (
                <IconRow purchase={purchase} item={item} />
            )}
        </div>
    );
};

const PackageIconRow = memo(({ purchase, item }: { purchase: Purchase; item: PurchaseItem }) => {
    const isStoreCredit = item.object === PurchaseItemObject.STORE_CREDIT;
    const { data: purchasedPackage } = useResource<Package | PartnerPackage>(["products", item.parentProduct?.id]);

    const totalQuantity =
        purchasedPackage && isSharedDemographicsPackage(purchasedPackage)
            ? item.quantity
            : sumBy(purchase.items, "quantity") || 1;

    const showGiftOrCreditBalance = isStoreCredit || purchase.items.every((item) => isGift(item));
    const giftCreditBalance = sumBy(purchase.items, "balance") ?? purchase.balance;
    const balance = showGiftOrCreditBalance ? giftCreditBalance : purchase.balance;

    const merchandiseItems = getMerchandisePurchaseItems(purchase);
    const hasMerchandise = merchandiseItems.length > 0;
    const merchandiseQuantity = sumBy(merchandiseItems, "quantity");
    const hasUnfulfilled = hasUnfulfilledMerchandise(merchandiseItems);

    return (
        <div className="flex flex-nowrap items-center justify-between">
            <div className="flex flex-wrap items-center">
                {isExperiencePurchaseItem(item) && (
                    <span className="text-sm lg:text-base" data-testid="purchase-list-item-total-quantity">
                        <span className="mr-2">
                            <UserIcon className="mr-1" />
                            {totalQuantity} Reserved
                        </span>
                    </span>
                )}
                {hasMerchandise && (
                    <span className="text-sm lg:text-base" data-testid="purchase-list-item-total-quantity">
                        <span className="mr-2 flex items-center">
                            <span
                                className={clsx(
                                    "mr-1 h-2 w-2 rounded-full",
                                    hasUnfulfilled ? "bg-warning" : "bg-success",
                                )}
                            />
                            <ShirtIcon className="mr-1" />
                            {merchandiseQuantity}
                        </span>
                    </span>
                )}
                {isDisputeSubmitted(purchase) ? (
                    <PurchaseDisputeBadge />
                ) : (
                    <BalanceBadge color={showGiftOrCreditBalance ? "success" : "danger"} value={balance} />
                )}
            </div>
            <span className="-mb-1 mr-4 flex min-w-5 justify-center">
                <StatusIcon skipConfirmed displayText={false} item={getDisplayItem(purchase)} />
            </span>
        </div>
    );
});

const IconRow = memo(({ purchase, item }: { purchase: Purchase; item: PurchaseItem }) => {
    const isStoreCredit = item.object === PurchaseItemObject.STORE_CREDIT;
    const totalQuantity = sumBy(purchase.items, "quantity") || 1;

    const showGiftOrCreditBalance = isStoreCredit || purchase.items.every((item) => isGift(item));
    const giftCreditBalance = sumBy(purchase.items, "balance") ?? purchase.balance;
    const balance = showGiftOrCreditBalance ? giftCreditBalance : purchase.balance;

    const isAllExperiencePurchase = purchase.items.every((item) => isExperiencePurchaseItem(item));
    const isAllEquipment = purchase.items.every((item) => isEquipmentItem(item));
    const isMixedItemTypes = purchase.items.some((item) => isEquipmentItem(item)) && !isAllEquipment;

    const isSameDemographic =
        !isMixedItemTypes &&
        isExperiencePurchaseItem(item) &&
        item.guests.every((guest, _, guests) => guest.unit.id === guests[0].unit.id);

    const { data: demographic } = useResource<Unit>(["units", item.guests?.[0]?.unit?.id], null, {
        enabled: isSameDemographic && !isOtherSellerPurchase(purchase, getSeller().id),
    });

    const merchandiseItems = getMerchandisePurchaseItems(purchase);
    const hasMerchandise = merchandiseItems.length > 0;
    const merchandiseQuantity = sumBy(merchandiseItems, "quantity");
    const hasUnfulfilled = hasUnfulfilledMerchandise(merchandiseItems);

    return (
        <div className="flex flex-nowrap items-center justify-between">
            <div className="flex flex-wrap items-center">
                {isExperiencePurchaseItem(item) && isAllExperiencePurchase && (
                    <span className="text-sm lg:text-base" data-testid="purchase-list-item-total-quantity">
                        {isMixedItemTypes ? (
                            <span className="mr-2">
                                <NoTranslate>{totalQuantity}</NoTranslate> Reserved
                            </span>
                        ) : isAllEquipment ? (
                            <EquipmentQuantityLabel items={purchase.items} totalQuantity={totalQuantity} />
                        ) : isSameDemographic && demographic && !isMistPurchase(purchase) ? (
                            <span className="mr-2">
                                <DemographicIcon className="mr-1" name={demographic.name} />
                                <NoTranslate>{totalQuantity}</NoTranslate>&nbsp;
                                <NoTranslate if={!isDefaultDemographic(demographic.name)}>
                                    {demographic.name}
                                </NoTranslate>
                            </span>
                        ) : (
                            <span className="mr-2">
                                <UserIcon className="mr-1" />
                                {totalQuantity} Reserved
                            </span>
                        )}
                    </span>
                )}

                {isMembershipItem(item) && !isMistPurchase(purchase) && (
                    <span className="text-sm lg:text-base" data-testid="purchase-list-item-total-quantity">
                        <span className="mr-2">
                            <PassIcon className="mr-1" />
                            {totalQuantity} {pluralize("Pass", totalQuantity)}
                        </span>
                    </span>
                )}
                {hasMerchandise && (
                    <span className="text-sm lg:text-base" data-testid="purchase-list-item-total-quantity">
                        <span className="mr-2 flex items-center">
                            <span
                                className={clsx(
                                    "mr-1 h-2 w-2 rounded-full",
                                    hasUnfulfilled ? "bg-warning" : "bg-success",
                                )}
                            />
                            <ShirtIcon className="mr-1" />
                            <NoTranslate>{merchandiseQuantity}</NoTranslate>
                        </span>
                    </span>
                )}

                {isDisputeSubmitted(purchase) ? (
                    <PurchaseDisputeBadge />
                ) : (
                    balance > 0 && (
                        <BalanceBadge color={showGiftOrCreditBalance ? "success" : "danger"} value={balance} />
                    )
                )}
            </div>
            <span className="-mb-1 mr-4 flex min-w-5 justify-center">
                <StatusIcon skipConfirmed displayText={false} item={getDisplayItem(purchase)} />
            </span>
        </div>
    );
});

const getPurchaseDate = (purchase: Purchase) => {
    const isMist = isMistPurchase(purchase);

    let earliestArrival: Dayjs | undefined;
    if (isMist) {
        for (const item of getScheduledExperiencePurchaseItems(purchase.items)) {
            if (!item.arrivalDateTime) {
                continue;
            }
            if (!earliestArrival || dayjs(item.arrivalDateTime).isBefore(dayjs(earliestArrival))) {
                earliestArrival = item.arrivalDateTime;
            }
        }
    } else {
        earliestArrival = getScheduledExperiencePurchaseItem(purchase.items)?.arrivalDateTime ?? purchase.createdAt;
    }
    const dateToShow = earliestArrival ?? purchase.createdAt;
    const isCurrentYear = dayjs(dateToShow).format("YYYY") === dayjs().format("YYYY");

    return isCurrentYear
        ? dayjs(dateToShow).format(DateFormat.MONTH_DATE_SHORT)
        : formatDate(dateToShow, DateFormat.DATE_SHORT);
};

export const getPurchaseItemArrivalDate = (item: PurchaseItem | GiftPurchaseItem): string | undefined => {
    const arrival = dayjs("arrivalDateTime" in item && item.arrivalDateTime ? item.arrivalDateTime : item.createdAt);
    const isCurrentYear = arrival.year() === dayjs().year();

    return isCurrentYear ? arrival.format(DateFormat.MONTH_DATE_SHORT) : formatDate(arrival, DateFormat.DATE_SHORT);
};

const getCommonEquipmentLabel = (items: PurchaseItem[]): string => {
    const names = items
        .flatMap((item) => item.lineItems ?? [])
        .filter((li) => li.type === LineItemType.EQUIPMENT && li.quantity)
        .map((li) => li.name)
        .filter(Boolean);
    const uniqueNames = [...new Set(names)];
    return uniqueNames.length === 1 ? uniqueNames[0]! : "Reserved";
};

const EquipmentQuantityLabel = ({ items, totalQuantity }: { items: PurchaseItem[]; totalQuantity: number }) => (
    <span className="mr-2">
        <EquipmentIcon className="mr-1" />
        <NoTranslate>{totalQuantity}</NoTranslate>&nbsp;
        <NoTranslate>{getCommonEquipmentLabel(items)}</NoTranslate>
    </span>
);

const getLogo = (purchase: Purchase, partnerSeller?: Seller) => {
    const isExpediaSource = purchase.source === BookingEngineSource.EXPEDIA;
    const isViatorSource = purchase.source === BookingEngineSource.VIATOR;

    if (isOtherSellerPurchase(purchase, getSeller().id) && partnerSeller) {
        return {
            icon: <SellerLogo seller={partnerSeller} size="small" />,
            className: "",
            style: { outline: "1.2px solid white" },
        };
    }

    if (isAffiliatePurchase(purchase)) {
        return {
            icon: <ReceptionBellIcon />,
            className: "inline-flex items-center justify-center bg-blue-lighter pt-0.5 text-primary",
            style: { outline: "1.2px solid white" },
        };
    }

    if (hasOtaTicket(purchase) || isViatorSource || isExpediaSource) {
        return {
            icon: (
                <SourceIcon
                    className={clsx("mt-[1px] ml-[1px]", { "mr-[1px]": isExpediaSource })}
                    source={purchase.source}
                    defaultIcon={EquipmentIcon}
                />
            ),
            className: clsx(
                isExpediaSource ? "bg-white" : "bg-blue-lighter",
                "text-black inline-flex items-center justify-center",
            ),
            style: isExpediaSource ? undefined : { outline: "1.2px solid white" },
        };
    }
};
